/**
 * AB#21539 — pure helpers behind the order Files tab. Everything the template
 * prints about a SharePoint drive item is computed here so it can be pinned by
 * unit tests without mounting the component.
 */
import type { DriveItem } from '@/types/sharepoint';

export type FilesView = 'list' | 'grid';
export type FilesSortKey = 'name' | 'modified';
export type FilesSortDir = 'ascending' | 'descending';

export const FILES_VIEW_PREF_KEY = 'orderFilesView';

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

export const readFilesView = (storage: StorageLike | null | undefined): FilesView => {
  try {
    return storage?.getItem(FILES_VIEW_PREF_KEY) === 'grid' ? 'grid' : 'list';
  } catch {
    return 'list';
  }
};

export const writeFilesView = (storage: StorageLike | null | undefined, view: FilesView): void => {
  try {
    storage?.setItem(FILES_VIEW_PREF_KEY, view);
  } catch {
    /* private mode / blocked storage — the choice just doesn't stick */
  }
};

/** File name without its extension; folders keep their whole name. */
export const baseName = (item: Pick<DriveItem, 'name' | 'type' | 'extension'>): string => {
  if (item.type !== 'file' || !item.extension) return item.name;
  const suffix = `.${item.extension}`;
  return item.name.toLowerCase().endsWith(suffix.toLowerCase())
    ? item.name.slice(0, -suffix.length)
    : item.name;
};

/** The API sends size in kilobytes, formatted ("1,234.56"). */
export const sizeKb = (item: Pick<DriveItem, 'size'>): number => {
  if (item.size === undefined || item.size === null || item.size === '') return 0;
  const n = typeof item.size === 'number' ? item.size : Number(String(item.size).replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};

export const sizeText = (item: Pick<DriveItem, 'size'>): string => {
  const kb = sizeKb(item);
  if (kb <= 0) return '—';
  if (kb < 1024) return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
  const gb = mb / 1024;
  return `${gb.toFixed(gb < 10 ? 2 : 1)} GB`;
};

/** What the Contents column says: item count for folders, size for files. */
export const contentsText = (item: Pick<DriveItem, 'type' | 'size' | 'child_count'>): string => {
  if (item.type === 'folder') {
    const n = item.child_count ?? 0;
    return n === 1 ? '1 item' : `${n} items`;
  }
  return sizeText(item);
};

/** "16 Sep 2026" or, with time, "16 Sep 2026, 14:05". Empty for no date. */
export const dateText = (iso: string | null | undefined, withTime = false): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const day = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  if (!withTime) return day;
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return `${day}, ${time}`;
};

/**
 * Graph reports automation identities (the Custimoo SharePoint app, service
 * accounts) as the editor. Those read as "System" in the UI; people keep their
 * own name.
 */
export const editorName = (name: string | null | undefined): string => {
  const trimmed = (name ?? '').trim();
  if (!trimmed) return '';
  if (/(^|[\s_-])app$|^system\b|service account|^sharepoint/i.test(trimmed)) return 'System';
  return trimmed;
};

export const initials = (name: string | null | undefined): string => {
  const shown = (name ?? '').trim().split('@')[0];
  const parts = shown.split(/[\s._-]+/).filter(Boolean);
  if (!parts.length) return '?';
  const first = parts[0][0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? '') : '';
  return (first + last).toUpperCase();
};

export const firstName = (name: string | null | undefined): string => {
  const trimmed = (name ?? '').trim();
  if (!trimmed) return '';
  if (trimmed.includes('@')) return trimmed.split('@')[0];
  return trimmed.split(/\s+/)[0];
};

const AVATAR_HUES = [212, 152, 268, 22, 340, 188, 44, 300];

/** A stable, muted colour per editor name so the same person always looks the same. */
export const avatarColor = (name: string | null | undefined): string => {
  const s = (name ?? '').trim().toLowerCase();
  let hash = 0;
  for (let i = 0; i < s.length; i += 1) hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  const hue = AVATAR_HUES[hash % AVATAR_HUES.length];
  return `hsl(${hue} 45% 42%)`;
};

/** Case-insensitive name match; empty query keeps everything. */
export const filterDriveItems = <T extends Pick<DriveItem, 'name'>>(
  items: T[],
  query: string
): T[] => {
  const q = query.trim().toLowerCase();
  if (!q) return items;
  return items.filter((item) => item.name.toLowerCase().includes(q));
};

/** Folders first, then the chosen key. Stable for equal keys. */
export const sortDriveItems = <T extends Pick<DriveItem, 'name' | 'type' | 'modified_at'>>(
  items: T[],
  key: FilesSortKey,
  dir: FilesSortDir
): T[] => {
  const sign = dir === 'descending' ? -1 : 1;
  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      if (a.item.type !== b.item.type) return a.item.type === 'folder' ? -1 : 1;
      let cmp = 0;
      if (key === 'modified') {
        const ta = a.item.modified_at ? Date.parse(a.item.modified_at) : 0;
        const tb = b.item.modified_at ? Date.parse(b.item.modified_at) : 0;
        cmp = ta - tb;
      }
      if (cmp === 0) {
        cmp = a.item.name.localeCompare(b.item.name, undefined, {
          numeric: true,
          sensitivity: 'base',
        });
      }
      return cmp * sign || a.index - b.index;
    })
    .map(({ item }) => item);
};

/** "3 folders · 12 files" / "1 file" / "Empty folder". */
export const folderSummaryText = (items: Pick<DriveItem, 'type'>[]): string => {
  const folders = items.filter((i) => i.type === 'folder').length;
  const files = items.length - folders;
  if (!items.length) return 'Empty folder';
  const parts: string[] = [];
  if (folders) parts.push(folders === 1 ? '1 folder' : `${folders} folders`);
  if (files) parts.push(files === 1 ? '1 file' : `${files} files`);
  return parts.join(' · ');
};

/** Footer line: what's shown, and what's selected. */
export const footText = (shown: number, total: number, selected: number): string => {
  const base =
    shown === total ? `${total} ${total === 1 ? 'item' : 'items'}` : `${shown} of ${total} items`;
  return selected ? `${base} · ${selected} selected` : base;
};

/** "Home" for the root, otherwise the relative folder path. */
export const folderLabel = (path: string | null | undefined): string => {
  const p = (path ?? '').replace(/^\/+|\/+$/g, '');
  return p ? p : 'Home';
};

/** Two folder paths name the same folder, ignoring root spellings and slashes. */
export const samePath = (a: string | null | undefined, b: string | null | undefined): boolean =>
  folderLabel(a) === folderLabel(b);

/** Local matches first (they are already on screen), then server results not yet shown. */
export const mergeSearchResults = <T extends Pick<DriveItem, 'id'>>(
  local: T[],
  remote: T[]
): T[] => {
  const seen = new Set(local.map((i) => i.id));
  return [...local, ...remote.filter((i) => !seen.has(i.id) && seen.add(i.id))];
};
